// WarrantyPolicy.jsx

import React, { useState, useEffect } from "react";

const sections = [
  {
    id: "01",
    title: "Warranty Coverage",
    content:
      "Tecniqa products are covered against manufacturing defects and workmanship issues under normal industrial and commercial usage conditions.",
  },
  {
    id: "02",
    title: "Warranty Duration",
    content:
      "Warranty duration may vary depending on the brand and product category. Certain products may carry manufacturer-backed warranties.",
  },
  {
    id: "03",
    title: "Eligible Products",
    content:
      "Only products purchased directly from Tecniqa or authorized sellers are eligible for warranty support.",
  },
  {
    id: "04",
    title: "Exclusions",
    content:
      "The warranty does not cover damages caused by improper installation, misuse, accidental damage, electrical fluctuations, unauthorized repairs, modifications, or normal wear and tear.",
  },
  {
    id: "05",
    title: "Warranty Claim Process",
    content:
      "Customers must provide proof of purchase, invoice details, and product information while requesting warranty support.",
  },
  {
    id: "06",
    title: "Inspection & Approval",
    content:
      "All warranty claims are subject to technical inspection and approval by Tecniqa or the respective manufacturer.",
  },
  {
    id: "07",
    title: "Repair or Replacement",
    content:
      "Products may be repaired, replaced, or serviced depending on inspection results and warranty eligibility.",
  },
  {
    id: "08",
    title: "Limitation of Liability",
    content:
      "Tecniqa shall not be liable for indirect, incidental, or consequential damages arising from product usage.",
  },
];

const trustItems = [
  "Genuine Industrial Products",
  "Manufacturer Warranty Support",
  "Secure Transactions",
  "Professional HVAC Assistance",
];

export default function WarrantyPolicy() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Warranty | Tecniqa";
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        :root {
          --dark: #1B211A;
          --green: #628141;
          --green-soft: #8BAE66;
          --cream: #EBD5AB;
          --white: #ffffff;
        }

        .warp-root {
          font-family: 'DM Sans', 'Segoe UI', sans-serif;
          background: #f7f7f5;
          min-height: 100vh;
        }

        /* ── HERO ── */
        .warp-hero {
          position: relative;
          padding: 56px 20px 100px;
          text-align: center;
          background: linear-gradient(145deg, #07140b 0%, #0d2414 50%, #17331f 100%);
          overflow: hidden;
        }

        .warp-hero-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(139, 174, 102, .07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 174, 102, .07) 1px, transparent 1px);
          background-size: 56px 56px;
          pointer-events: none;
        }

        .warp-hero-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
        }

        .warp-orb1 {
          width: 380px; height: 380px;
          top: -120px; left: -80px;
          background: rgba(139, 174, 102, .16);
        }

        .warp-orb2 {
          width: 280px; height: 280px;
          bottom: -80px; right: -60px;
          background: rgba(98, 129, 65, .18);
        }

        .warp-hero-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .warp-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(139, 174, 102, .12);
          border: 1px solid rgba(139, 174, 102, .26);
          border-radius: 999px;
          padding: 6px 18px;
          margin-bottom: 22px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: #8BAE66;
        }

        .warp-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #8BAE66;
          box-shadow: 0 0 6px #8BAE66;
          animation: warpPulse 1.6s ease infinite;
        }

        @keyframes warpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .45; transform: scale(.7); }
        }

        .warp-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.2rem, 5vw, 3.8rem);
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin-bottom: 14px;
        }

        .warp-hero-title span { color: #8BAE66; }

        .warp-hero-sub {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.62);
          font-weight: 300;
          line-height: 1.8;
          max-width: 500px;
          margin-bottom: 24px;
        }

        .warp-hero-meta {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .warp-meta-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(98, 129, 65, 0.18);
          border: 1px solid rgba(98, 129, 65, 0.32);
          color: #8BAE66;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 14px;
          border-radius: 20px;
          letter-spacing: 0.05em;
        }

        /* ── TRUST STRIP ── */
        .warp-trust {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: #fff;
          border-bottom: 1px solid #ebebeb;
          position: relative;
          z-index: 2;
        }

        .warp-trust-item {
          padding: 20px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #1B211A;
          border-right: 1px solid #ebebeb;
          letter-spacing: 0.01em;
        }

        .warp-trust-item:last-child { border-right: none; }

        .warp-trust-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #628141;
          flex-shrink: 0;
        }

        /* ── BODY ── */
        .warp-body {
          max-width: 920px;
          margin: 0 auto;
          padding: 50px 32px 80px;
        }

        /* ── INTRO CARD ── */
        .warp-intro-card {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #ebebeb;
          border-left: 4px solid #628141;
          padding: 24px 28px;
          margin-top: -32px;
          margin-bottom: 36px;
          position: relative;
          z-index: 2;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
        }

        .warp-intro-card p {
          font-size: 14.5px;
          line-height: 1.85;
          color: #555;
          font-weight: 400;
        }

        .warp-intro-card strong {
          color: #111;
          font-weight: 700;
        }

        /* ── ACCORDION ── */
        .warp-sections {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 48px;
        }

        .warp-section {
          background: #fff;
          border: 1px solid #ebebeb;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .warp-section:hover {
          border-color: rgba(98, 129, 65, 0.4);
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
        }

        .warp-section--open {
          border-color: #628141;
          box-shadow: 0 4px 20px rgba(98, 129, 65, 0.12);
        }

        .warp-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 22px;
          user-select: none;
        }

        .warp-section-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .warp-section-num {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #628141;
          background: rgba(98,129,65,0.1);
          border: 1px solid rgba(98,129,65,0.2);
          border-radius: 6px;
          padding: 3px 8px;
          min-width: 36px;
          text-align: center;
        }

        .warp-section-title {
          font-size: 14.5px;
          font-weight: 600;
          color: #111;
          letter-spacing: 0.01em;
        }

        .warp-chevron {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(98,129,65,0.08);
          border: 1px solid rgba(98,129,65,0.18);
          color: #628141;
          font-size: 18px;
          font-weight: 300;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.2s, color 0.2s;
          line-height: 1;
        }

        .warp-section--open .warp-chevron {
          background: #628141;
          color: #fff;
          border-color: #628141;
        }

        .warp-section-body {
          padding: 0 22px 20px 74px;
          border-top: 1px solid rgba(98, 129, 65, 0.1);
        }

        .warp-section-body p {
          padding-top: 16px;
          font-size: 14px;
          line-height: 1.9;
          color: #666;
          font-weight: 400;
        }

        /* ── FOOTER NOTE ── */
        .warp-footer-note {
          background: linear-gradient(135deg, #1B211A 0%, #253919 40%, #223616 70%, #3e5326 100%);
          border-radius: 14px;
          padding: 28px 32px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.18);
        }

        .warp-footer-note::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #628141, #8BAE66, #628141);
        }

        .warp-footer-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #8BAE66;
        }

        .warp-footer-note p {
          font-size: 14px;
          color: rgba(255,255,255,0.65);
          line-height: 1.7;
          font-weight: 300;
        }

        .warp-footer-note a {
          color: #EBD5AB;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid rgba(235,213,171,0.3);
          transition: border-color 0.2s;
        }

        .warp-footer-note a:hover { border-color: #EBD5AB; }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .warp-trust { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 640px) {
          .warp-body { padding: 0 16px 60px; }
          .warp-intro-card { margin-top: 15px; padding: 18px 20px; }
          .warp-section-body { padding-left: 22px; }
          .warp-footer-note { padding: 22px 20px; }
          .warp-hero { padding: 40px 16px 80px; }
          .warp-trust { grid-template-columns: 1fr; }
          .warp-trust-item { border-right: none; border-bottom: 1px solid #ebebeb; }
          .warp-trust-item:last-child { border-bottom: none; }
        }
      `}</style>

      <div className="warp-root">

        {/* HERO */}
        <div className="warp-hero">
          <div className="warp-hero-grid" />
          <div className="warp-hero-orb warp-orb1" />
          <div className="warp-hero-orb warp-orb2" />

          <div className="warp-hero-content">
            <div className="warp-hero-badge">
              <div className="warp-badge-dot" />
              Warranty Protection
            </div>

            <h1 className="warp-hero-title">
              Warranty Policy,<br />
              <span>Your Assurance</span>
            </h1>

            <p className="warp-hero-sub">
              Tecniqa is committed to delivering reliable industrial-grade HVACR products
              backed by professional warranty support and trusted service standards.
            </p>

            <div className="warp-hero-meta">
              <span className="warp-meta-badge">Effective: May 2026</span>
              <span className="warp-meta-badge">8 Sections</span>
            </div>
          </div>
        </div>

        {/* TRUST STRIP */}
        <div className="warp-trust">
          {trustItems.map((item, i) => (
            <div className="warp-trust-item" key={i}>
              <div className="warp-trust-dot" />
              {item}
            </div>
          ))}
        </div>

        {/* BODY */}
        <div className="warp-body">

          {/* INTRO CARD */}
          <div className="warp-intro-card">
            <p>
              At <strong>Tecniqa</strong>, we stand behind every product we sell. This Warranty
              Policy outlines the coverage, eligibility, and claim process for products purchased
              through the Tecniqa platform. We are committed to resolving all legitimate warranty
              concerns promptly and professionally.
            </p>
          </div>

          {/* ACCORDION */}
          <div className="warp-sections">
            {sections.map((sec) => {
              const isOpen = active === sec.id;
              return (
                <div
                  key={sec.id}
                  className={`warp-section${isOpen ? " warp-section--open" : ""}`}
                  onClick={() => setActive(isOpen ? null : sec.id)}
                >
                  <div className="warp-section-header">
                    <div className="warp-section-left">
                      <span className="warp-section-num">{sec.id}</span>
                      <span className="warp-section-title">{sec.title}</span>
                    </div>
                    <span className="warp-chevron">{isOpen ? "−" : "+"}</span>
                  </div>
                  {isOpen && (
                    <div className="warp-section-body">
                      <p>{sec.content}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* FOOTER NOTE */}
          <div className="warp-footer-note">
            <span className="warp-footer-label">Need warranty assistance?</span>
            <p>
              Contact our support team at{" "}
              <a href="mailto:support@tecniqa.com">support@tecniqa.com</a> or call{" "}
              <a href="tel:+919668246683">+91 9668 246 683</a> during business hours (Mon–Sat, 9 AM – 6 PM).
            </p>
          </div>

        </div>
      </div>
    </>
  );
}